/**
 * CS Category Hooks
 *
 * Two families, matching the service split:
 *  - BU-scoped hooks (`use...CSCategor...`) read/write the caller's own account's copies,
 *    scoped with `buScopeParams`/`buScopeKey` the same way `use-guardrails.ts` is.
 *  - Template hooks (`use...CSCategoryTemplate...`) are super-admin only and unscoped.
 */

import { useAtom } from 'jotai';
import { atom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { csCategoryService } from '@/services';
import { invalidateQueries } from '@/lib/query-client';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';
import type { CSCategoryQueryParams, PropagateRequestPayload } from '@/services/csCategory.service';

// ============================================================================
// BU-scoped (account's own intents)
// ============================================================================

// "Published" pre-dates the BU-scoping work, when drafts were still a thing categories
// could be in. Drafts are retired now — every row is published — so this filters to the
// enabled ones instead, which is what every caller actually wants: something selectable.
const publishedCsCategoriesQueryAtom = atomWithQuery((get) => ({
  queryKey: ['cs-categories', 'published', buScopeKey(get)],
  queryFn: async () => {
    const categories = await csCategoryService.getCategories(buScopeParams(get));
    return categories.filter((category) => category.isEnabled !== false);
  }
}));

const csCategoriesPaginationAtom = atom<{ page: number; limit: number }>({ page: 1, limit: 20 });

const csCategoriesPageFiltersAtom = atom<Omit<CSCategoryQueryParams, 'page' | 'limit'>>({});

const csCategoriesPagedQueryAtom = atomWithQuery((get) => {
  const { page, limit } = get(csCategoriesPaginationAtom);
  const filters = get(csCategoriesPageFiltersAtom);
  return {
    queryKey: ['cs-categories', 'paged', page, limit, filters, buScopeKey(get)],
    queryFn: () => csCategoryService.getCategoriesPaged({ ...filters, page, limit }, buScopeParams(get)),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false
  };
});

const csCategoryFilterOptionsQueryAtom = atomWithQuery((get) => ({
  queryKey: ['cs-categories', 'filter-options', buScopeKey(get)],
  queryFn: () => csCategoryService.getFilterOptions(buScopeParams(get)),
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false
}));

const createCSCategoryMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['cs-categories', 'create'],
  mutationFn: (data: Parameters<typeof csCategoryService.createCategory>[0]) =>
    csCategoryService.createCategory(data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['cs-categories']);
  }
}));

const updateCSCategoryMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['cs-categories', 'update'],
  mutationFn: ({ id, data }: { id: string; data: Parameters<typeof csCategoryService.updateCategory>[1] }) =>
    csCategoryService.updateCategory(id, data, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['cs-categories']);
  }
}));

const setCSCategoryEnabledMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['cs-categories', 'set-enabled'],
  mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
    csCategoryService.setCategoryEnabled(id, isEnabled, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['cs-categories']);
  }
}));

const deleteCSCategoryMutationAtom = atomWithMutation((get) => ({
  mutationKey: ['cs-categories', 'delete'],
  mutationFn: (id: string) => csCategoryService.deleteCategory(id, buScopeParams(get)),
  onSuccess: () => {
    invalidateQueries(['cs-categories']);
  }
}));

export function usePublishedCSCategories() {
  const [query] = useAtom(publishedCsCategoriesQueryAtom);
  return query;
}

export function useCSCategoriesPaged() {
  const [query] = useAtom(csCategoriesPagedQueryAtom);
  return query;
}

export function useCSCategoriesPagination() {
  return useAtom(csCategoriesPaginationAtom);
}

export function useCSCategoriesPageFilters() {
  return useAtom(csCategoriesPageFiltersAtom);
}

export function useCSCategoryFilterOptions() {
  const [query] = useAtom(csCategoryFilterOptionsQueryAtom);
  return query;
}

export function useCreateCSCategory() {
  const [mutation] = useAtom(createCSCategoryMutationAtom);
  return mutation;
}

export function useUpdateCSCategory() {
  const [mutation] = useAtom(updateCSCategoryMutationAtom);
  return mutation;
}

export function useSetCSCategoryEnabled() {
  const [mutation] = useAtom(setCSCategoryEnabledMutationAtom);
  return mutation;
}

export function useDeleteCSCategory() {
  const [mutation] = useAtom(deleteCSCategoryMutationAtom);
  return mutation;
}

// ============================================================================
// Templates (super-admin only, unscoped)
// ============================================================================

const csCategoryTemplatesPaginationAtom = atom<{ page: number; limit: number }>({ page: 1, limit: 20 });

const csCategoryTemplatesPageFiltersAtom = atom<Omit<CSCategoryQueryParams, 'page' | 'limit'>>({});

const csCategoryTemplatesPagedQueryAtom = atomWithQuery((get) => {
  const { page, limit } = get(csCategoryTemplatesPaginationAtom);
  const filters = get(csCategoryTemplatesPageFiltersAtom);
  return {
    queryKey: ['cs-categories-templates', 'paged', page, limit, filters],
    queryFn: () => csCategoryService.getTemplates({ ...filters, page, limit }),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false
  };
});

const csCategoryTemplateFilterOptionsQueryAtom = atomWithQuery(() => ({
  queryKey: ['cs-categories-templates', 'filter-options'],
  queryFn: () => csCategoryService.getTemplateOptions(),
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false
}));

const createCSCategoryTemplateMutationAtom = atomWithMutation(() => ({
  mutationKey: ['cs-categories-templates', 'create'],
  mutationFn: (data: Parameters<typeof csCategoryService.createTemplate>[0]) =>
    csCategoryService.createTemplate(data),
  onSuccess: () => {
    invalidateQueries(['cs-categories-templates']);
  }
}));

const updateCSCategoryTemplateMutationAtom = atomWithMutation(() => ({
  mutationKey: ['cs-categories-templates', 'update'],
  mutationFn: ({ id, data }: { id: string; data: Parameters<typeof csCategoryService.updateTemplate>[1] }) =>
    csCategoryService.updateTemplate(id, data),
  onSuccess: () => {
    invalidateQueries(['cs-categories-templates']);
  }
}));

const setCSCategoryTemplateEnabledMutationAtom = atomWithMutation(() => ({
  mutationKey: ['cs-categories-templates', 'set-enabled'],
  mutationFn: ({ id, isEnabled }: { id: string; isEnabled: boolean }) =>
    csCategoryService.setTemplateEnabled(id, isEnabled),
  onSuccess: () => {
    invalidateQueries(['cs-categories-templates']);
  }
}));

const deleteCSCategoryTemplateMutationAtom = atomWithMutation(() => ({
  mutationKey: ['cs-categories-templates', 'delete'],
  mutationFn: (id: string) => csCategoryService.deleteTemplate(id),
  onSuccess: () => {
    invalidateQueries(['cs-categories-templates']);
  }
}));

const csCategoryPropagateBusinessUnitsQueryAtom = atomWithQuery(() => ({
  queryKey: ['cs-categories-templates', 'business-units'],
  queryFn: () => csCategoryService.getPropagateBusinessUnits(),
  staleTime: 60 * 1000,
  refetchOnWindowFocus: false
}));

const previewCSCategoryPropagateMutationAtom = atomWithMutation(() => ({
  mutationKey: ['cs-categories-templates', 'propagate-preview'],
  mutationFn: ({ id, payload }: { id: string; payload: PropagateRequestPayload }) =>
    csCategoryService.previewPropagate(id, payload)
}));

const applyCSCategoryPropagateMutationAtom = atomWithMutation(() => ({
  mutationKey: ['cs-categories-templates', 'propagate-apply'],
  mutationFn: ({ id, payload }: { id: string; payload: PropagateRequestPayload }) =>
    csCategoryService.applyPropagate(id, payload),
  onSuccess: () => {
    // The BU-scoped lists (every account/brand) may now differ from what propagated.
    invalidateQueries(['cs-categories']);
  }
}));

export function useCSCategoryTemplatesPaged() {
  const [query] = useAtom(csCategoryTemplatesPagedQueryAtom);
  return query;
}

export function useCSCategoryTemplatesPagination() {
  return useAtom(csCategoryTemplatesPaginationAtom);
}

export function useCSCategoryTemplatesPageFilters() {
  return useAtom(csCategoryTemplatesPageFiltersAtom);
}

export function useCSCategoryTemplateFilterOptions() {
  const [query] = useAtom(csCategoryTemplateFilterOptionsQueryAtom);
  return query;
}

export function useCreateCSCategoryTemplate() {
  const [mutation] = useAtom(createCSCategoryTemplateMutationAtom);
  return mutation;
}

export function useUpdateCSCategoryTemplate() {
  const [mutation] = useAtom(updateCSCategoryTemplateMutationAtom);
  return mutation;
}

export function useSetCSCategoryTemplateEnabled() {
  const [mutation] = useAtom(setCSCategoryTemplateEnabledMutationAtom);
  return mutation;
}

export function useDeleteCSCategoryTemplate() {
  const [mutation] = useAtom(deleteCSCategoryTemplateMutationAtom);
  return mutation;
}

export function useCSCategoryPropagateBusinessUnits() {
  const [query] = useAtom(csCategoryPropagateBusinessUnitsQueryAtom);
  return query;
}

export function usePreviewCSCategoryPropagate() {
  const [mutation] = useAtom(previewCSCategoryPropagateMutationAtom);
  return mutation;
}

export function useApplyCSCategoryPropagate() {
  const [mutation] = useAtom(applyCSCategoryPropagateMutationAtom);
  return mutation;
}
